import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, LineChart, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { formatRangeText, formatUsd, stripMake } from '@siliconx/shared/fmv/browse';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { Field, TextInput } from '@siliconx/shared/ui/Field';
import type {
  ConditionBand,
  PurchaseAnalysisDetailResponse,
  PurchaseAnalysisLineRow,
  PurchaseComputeResponse,
} from '@siliconx/shared';
import { CalculationBasisTrigger } from '../../components/financials/CalculationBasisDrawer';
import { ExportCsvButton } from '../../components/financials/ExportButtons';
import { AddFromListingButton } from '../../components/market-intelligence/LotPicker';
import { AddClassLineButton } from '../../components/market-intelligence/ClassPicker';
import { SkuLinePicker } from '../../components/market-intelligence/SkuLinePicker';
import { PurchaseMarginCurveChart } from '../../components/market-intelligence/PurchaseMarginCurveChart';
import { api, throwIfNotOk, unwrap } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { useAuth } from '../../lib/authContext';

/**
 * SIL-1072 — the purchase-analysis builder (design spec §4 "The builder").
 * Three regions: lines table, collapsed-by-default costs panel, result
 * header. The bid field recomputes live through the transient override body
 * on POST /:id/compute (debounced) — nothing is saved until the user acts.
 *
 * Buyer/seller framing (spec §4 "Buyer and seller framing") is copy + one
 * default only: for canSell && !canBuy, the bid column is labelled "cost
 * basis" and defaults to zero, and the headline output is the best month to
 * sell rather than the break-even bid. Same screen, same engine, same
 * endpoints — no second code path.
 */
export const Route = createFileRoute('/market-intelligence/$analysisId')({
  component: PurchaseAnalysisBuilder,
});

const CONDITION_BANDS: ConditionBand[] = ['new', 'like_new', 'normal_enterprise_wear', 'high_wear', 'unknown'];

function fmtUsd(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}
function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function PurchaseAnalysisBuilder() {
  const { analysisId } = Route.useParams();
  const { canBuy, canSell } = useAuth();
  const sellerFraming = canSell && !canBuy;
  const queryClient = useQueryClient();

  const detailQ = useQuery<PurchaseAnalysisDetailResponse>({
    queryKey: qk.marketIntel.analysis(analysisId),
    queryFn: async () =>
      unwrap(await api.api.client['purchase-analyses'][':id'].$get({ param: { id: analysisId } })),
  });

  const [bidOverrides, setBidOverrides] = useState<Record<string, number>>({});
  const [skuPanelOpen, setSkuPanelOpen] = useState(false);
  const [costsOpen, setCostsOpen] = useState(false);
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);
  const [compute, setCompute] = useState<PurchaseComputeResponse | null>(null);
  const [computing, setComputing] = useState(false);
  const [computeError, setComputeError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lines = detailQ.data?.lines ?? [];

  async function runPreview(nextOverrides: Record<string, number>) {
    setComputing(true);
    try {
      const result = await unwrap(
        await api.api.client['purchase-analyses'][':id'].compute.$post({
          param: { id: analysisId },
          json: {
            lineOverrides: Object.entries(nextOverrides).map(([lineId, unitAcquisitionCostUsd]) => ({
              lineId,
              unitAcquisitionCostUsd,
            })),
            costOverrides: [],
          },
        }),
      );
      setCompute(result);
      setComputeError(false);
    } catch {
      setComputeError(true);
    } finally {
      setComputing(false);
    }
  }

  // Live debounced preview whenever lines change (load or edit).
  useEffect(() => {
    if (lines.length === 0) {
      setCompute(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runPreview(bidOverrides);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.length, bidOverrides, analysisId]);

  function setBid(lineId: string, value: number) {
    setBidOverrides((prev) => ({ ...prev, [lineId]: value }));
  }

  async function removeLine(lineId: string) {
    const res = await api.api.client['purchase-analyses'][':id'].lines[':lineId'].$delete({
      param: { id: analysisId, lineId },
    });
    await throwIfNotOk(res);
    void queryClient.invalidateQueries({ queryKey: qk.marketIntel.analysis(analysisId) });
  }

  async function resetCosts() {
    const res = await api.api.client['purchase-analyses'][':id']['costs']['reset'].$post({ param: { id: analysisId } });
    await throwIfNotOk(res);
    void queryClient.invalidateQueries({ queryKey: qk.marketIntel.analysis(analysisId) });
  }

  const lineResultById = useMemo(() => {
    const map = new Map<string, PurchaseComputeResponse['lines'][number]>();
    for (const l of compute?.lines ?? []) map.set(l.lineId, l);
    return map;
  }, [compute]);

  return (
    <div className="space-y-6">
      <PageHeader title={detailQ.data?.analysis.name ?? 'Purchase analysis'} pinTo="/market-intelligence" />

      <QueryBoundary query={detailQ} errorTitle="Couldn't load this analysis.">
        {(detail) => (
          <>
            {!detail.costsConfigured && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>
                  No cost constant has a non-zero value on this analysis. Margin below is revenue minus bid, with
                  no cost basis — set cost constants before trusting this number.
                </span>
              </div>
            )}

            {/* Result header */}
            <Card as="section" className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">
                  {sellerFraming ? 'Best month to sell' : 'Result'}
                </h2>
                <div className="flex items-center gap-2">
                  {compute && <CalculationBasisTrigger title="Purchase analysis" basis={compute.basis} />}
                  {compute && lines.length > 0 && (
                    <ExportCsvButton
                      path={`/api/client/purchase-analyses/${analysisId}/export/analysis.csv`}
                      filename="purchase-analysis.csv"
                    />
                  )}
                </div>
              </div>
              {compute ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <Stat label="Total bid" value={fmtUsd(compute.totalBidUsd)} />
                  <Stat label="Total revenue" value={fmtUsd(compute.totalRevenueUsd)} />
                  <Stat label="Total margin" value={fmtUsd(compute.totalMarginUsd)} />
                  <Stat label="Blended margin %" value={fmtPct(compute.blendedMarginPct)} />
                </div>
              ) : (
                <p className={computeError && !computing ? 'text-sm text-red-600' : 'text-sm text-muted-foreground'}>
                  {computing
                    ? 'Computing…'
                    : computeError
                      ? "Couldn't compute results. Try again later."
                      : 'Add a line to see results.'}
                </p>
              )}
              {compute && compute.linesWithSuppressedPriceCount > 0 && (
                <p className="text-xs text-amber-700">
                  {compute.linesWithSuppressedPriceCount === lines.length
                    ? 'No line has enough comparable sales to price, so totals are empty.'
                    : `${compute.linesWithSuppressedPriceCount} of ${lines.length} lines excluded from totals: too few comparable sales to price.`}
                </p>
              )}
              {compute && (
                <p className="text-xs text-muted-foreground">
                  Computed against the forecast run of{' '}
                  <span className="font-medium">{compute.stamp.forecastAsOf ?? 'no forecast available'}</span>.
                  {compute.worstConfidenceGrade && (compute.worstConfidenceGrade === 'C' || compute.worstConfidenceGrade === 'D') && (
                    <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
                      Confidence {compute.worstConfidenceGrade}
                    </span>
                  )}
                </p>
              )}
            </Card>

            {/* Lines table */}
            <Card as="section" className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">Lines</h2>
                <div className="flex gap-2">
                  <AddFromListingButton
                    analysisId={analysisId}
                    onImported={() =>
                      void queryClient.invalidateQueries({ queryKey: qk.marketIntel.analysis(analysisId) })
                    }
                  />
                  <AddClassLineButton
                    analysisId={analysisId}
                    onAdded={() =>
                      void queryClient.invalidateQueries({ queryKey: qk.marketIntel.analysis(analysisId) })
                    }
                  />
                  <Button type="button" onClick={() => setSkuPanelOpen((v) => !v)}>
                    <Plus className="mr-1 size-4" /> Add SKU line
                  </Button>
                </div>
              </div>
              {skuPanelOpen && (
                <SkuLinePicker
                  analysisId={analysisId}
                  onAdded={() => {
                    setSkuPanelOpen(false);
                    void queryClient.invalidateQueries({ queryKey: qk.marketIntel.analysis(analysisId) });
                  }}
                  onCancel={() => setSkuPanelOpen(false)}
                />
              )}
              {lines.length === 0 ? (
                <p className="text-sm text-muted-foreground">No lines yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[860px] text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                        <th className="py-2">Target</th>
                        <th>FMV</th>
                        <th>Qty</th>
                        <th>Condition</th>
                        <th>{sellerFraming ? 'Cost basis' : 'Bid'}</th>
                        <th>Margin / unit</th>
                        <th>Best month</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <LineRow
                          key={line.id}
                          line={line}
                          sellerFraming={sellerFraming}
                          bid={bidOverrides[line.id] ?? line.unitAcquisitionCostUsd}
                          onBidChange={(v) => setBid(line.id, v)}
                          result={lineResultById.get(line.id)}
                          onRemove={() => void removeLine(line.id)}
                          expanded={expandedLineId === line.id}
                          onToggleExpand={() =>
                            setExpandedLineId((prev) => (prev === line.id ? null : line.id))
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Costs panel — collapsed by default */}
            <Card as="section" className="space-y-2">
              <button
                type="button"
                onClick={() => setCostsOpen((v) => !v)}
                className="flex w-full items-center justify-between text-left"
              >
                <h2 className="text-sm font-semibold text-foreground">Costs</h2>
                {costsOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              </button>
              {costsOpen && (
                <div className="space-y-2">
                  <div className="flex justify-end">
                    <Button type="button" onClick={() => void resetCosts()}>
                      <RotateCcw className="mr-1 size-4" /> Reset from house rates
                    </Button>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                        <th className="py-2">Label</th>
                        <th>Kind</th>
                        <th>Value</th>
                        <th>Overridden</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.costs.map((c) => (
                        <tr key={c.key} className="border-b border-border last:border-b-0">
                          <td className="py-2">{c.label}</td>
                          <td className="text-xs text-muted-foreground">{c.kind}</td>
                          <td>{c.kind.startsWith('pct_') ? fmtPct(c.value) : fmtUsd(c.value)}</td>
                          <td>{c.overriddenAt ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}

export function LineRow({
  line,
  sellerFraming,
  bid,
  onBidChange,
  result,
  onRemove,
  expanded,
  onToggleExpand,
}: {
  line: PurchaseAnalysisLineRow;
  sellerFraming: boolean;
  bid: number;
  onBidChange: (v: number) => void;
  result: PurchaseComputeResponse['lines'][number] | undefined;
  onRemove: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const [bidDraft, setBidDraft] = useState(String(bid));
  const hasCurve = !!result && result.forecastAvailable && result.curve.length > 1;
  const rawId = line.skuId ?? line.assetClassId ?? undefined;
  return (
    <>
      <tr className="border-b border-border last:border-b-0">
        <td className="py-2" title={rawId}>
          <LineTargetLabel line={line} />
          {line.sourceListingId && (
            <span
              className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              title={`Imported from marketplace listing ${line.sourceListingId}`}
            >
              from lot {line.sourceListingId.slice(0, 8)}
            </span>
          )}
        </td>
        <td>
          <LineFmvCell line={line} />
        </td>
        <td>{line.quantity}</td>
        <td>
          <select
            defaultValue={line.conditionBand}
            className="rounded-md border border-border px-2 py-1 text-xs"
            disabled
          >
            {CONDITION_BANDS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </td>
        <td>
          <TextInput
            type="number"
            min={0}
            step="0.01"
            value={bidDraft}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              // Keep the raw text so the field can be empty; Number('') is 0,
              // which wrote the 0 back and turned a typed 7400 into "07400".
              setBidDraft(e.target.value);
              if (e.target.value !== '') onBidChange(Number(e.target.value));
            }}
            className="w-28"
          />
        </td>
        <td>
          {result ? (
            result.currentPriceSuppressed ? (
              <span className="text-xs text-amber-700">too few comparable sales to price this</span>
            ) : !result.forecastAvailable ? (
              <span className="text-xs text-muted-foreground">{fmtUsd(result.sellNow.marginUsd)} (sell-now only)</span>
            ) : (
              fmtUsd(result.atBestMonth.marginUsd)
            )
          ) : (
            '—'
          )}
        </td>
        <td>{sellerFraming ? (result ? `Month ${result.bestMonth}` : '—') : result ? `Month ${result.bestMonth}` : '—'}</td>
        <td className="flex items-center gap-1">
          {hasCurve && (
            <button
              type="button"
              onClick={onToggleExpand}
              aria-label={expanded ? 'Hide margin curve' : 'Show margin curve'}
              className={cn(
                'rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground',
                expanded && 'bg-muted text-foreground',
              )}
            >
              <LineChart className="size-4" />
            </button>
          )}
          <button type="button" onClick={onRemove} aria-label="Remove line" className={cn('rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-red-600')}>
            <Trash2 className="size-4" />
          </button>
        </td>
      </tr>
      {expanded && hasCurve && result && (
        <tr className="border-b border-border last:border-b-0">
          <td colSpan={8} className="bg-muted/20 px-2 py-3">
            <PurchaseMarginCurveChart result={result} />
          </td>
        </tr>
      )}
    </>
  );
}

/** First-cell label (spec follow-up: "do not display the sku_id in the
 * table, show the sku model name, with manufacturer"). `stripMake` avoids
 * "Dell Dell R750" when the catalog's modelName already carries the make as
 * a prefix — same helper MakeModelPicker/SkuLinePicker use at pick time. A
 * sku line whose display fields never resolved (deleted/retired sku) and an
 * asset-class line whose class name never resolved both fall back to a
 * muted "Unknown SKU" — the raw UUID stays out of the visible text (it is
 * still available via the row's `title` tooltip). */
function LineTargetLabel({ line }: { line: PurchaseAnalysisLineRow }) {
  if (line.lineType === 'asset_class') {
    return line.assetClassName ? (
      <span className="font-medium text-foreground">{line.assetClassName}</span>
    ) : (
      <span className="text-muted-foreground">Unknown asset class</span>
    );
  }

  if (!line.modelName) {
    return <span className="text-muted-foreground">Unknown SKU</span>;
  }

  const model = line.manufacturerName ? stripMake(line.modelName, line.manufacturerName) : line.modelName;
  return (
    <span className="font-medium text-foreground">
      {line.manufacturerName ? `${line.manufacturerName} ${model}` : model}
      {line.grain === 'master' && (
        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
          family
        </span>
      )}
    </span>
  );
}

/** FMV column (spec follow-up: "display the fmv values as well ... they are
 * currently lost in the display"). A master prices as a family range, never
 * a point price (same convention as SkuLinePicker's `priceLabel`); a
 * variant/standalone sku shows its baseline plus the low-high range. Null
 * across the board (asset-class line, or a suppressed/unresolved sku) shows
 * a muted "no FMV" marker, never a 0. */
function LineFmvCell({ line }: { line: PurchaseAnalysisLineRow }) {
  if (line.lineType === 'asset_class') {
    return <span className="text-xs text-muted-foreground">n/a</span>;
  }
  const range = formatRangeText(line.fmvRangeLowUsd, line.fmvRangeHighUsd);
  if (line.grain === 'master') {
    return range !== '—' ? (
      <span className="text-xs text-foreground">{range}</span>
    ) : (
      <span className="text-xs text-muted-foreground">no FMV</span>
    );
  }
  if (line.fmvBaselineUsd === null) {
    return <span className="text-xs text-muted-foreground">no FMV</span>;
  }
  return (
    <span className="text-xs text-foreground">
      {formatUsd(line.fmvBaselineUsd)}
      {range !== '—' && <span className="ml-1 text-muted-foreground">({range})</span>}
    </span>
  );
}
